import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersistence from 'vuex-persist';
import { devices } from './modules/devices';
import { batches } from './modules/batches';
import { versions } from './modules/versions';
import { projects } from './modules/projects';
import { locations } from './modules/locations';
import { sites } from './modules/sites';
import { users } from './modules/users';
import { tasks } from './modules/tasks';
import { loading } from './modules/loading';
import { login } from './modules/login';
import { applications } from './modules/applications';
import { adminApplications} from './modules/admin.applications';
import { organisations } from './modules/organisations';
import { notifications } from './modules/notifications';
import { sidebar } from './modules/sidebar';
import { roles } from './modules/roles';
import { reporting } from './modules/reporting';
import { widget } from './modules/widget';
import { rules } from './modules/rules';
import { dataSets } from './modules/datasets';
import { locationsV2 } from './modules/locations.v2';
import { jobs } from './modules/jobs';
import { explorer } from './modules/data.explorer';
import { datatable } from './modules/datatable';
import { subscriptions } from './modules/subscriptions';
import { accounts } from './modules/accounts';
import { gateways } from './modules/gateways';

import {
  DeviceBatchState,
  DeviceState,
  GatewayState,
  DeviceVersionState,
  LoadingState,
  LocationState,
  LoginState,
  OrganisationState,
  ProjectState,
  SiteState,
  UserState,
  NotificationState,
  SidebarState,
  AdminDeviceTypeState,
  RoleState,
  ReportingState,
  WidgetState,
  RulesState,
  DataSetState,
  LocationsV2State,
  JobState,
  DataExplorerState,
  DataTableState,
  SubscriptionsState,
  AccountState,
} from '@/types';

export interface State {
  users: UserState;
  devices: DeviceState;
  gateways: GatewayState;
  login: LoginState;
  loading: LoadingState;
  organisations: OrganisationState;
  versions: DeviceVersionState;
  batches: DeviceBatchState;
  projects: ProjectState;
  sites: SiteState;
  locations: LocationState;
  notifications: NotificationState;
  sidebar: SidebarState;
  roles: RoleState;
  reporting: ReportingState;
  widget: WidgetState;
  rules: RulesState;
  dataSets: DataSetState;
  locationsV2: LocationsV2State;
  jobs: JobState;
  explorer: DataExplorerState;
  datatable: DataTableState;
  subscriptions: SubscriptionsState;
  accounts: AccountState;
}

Vue.use(Vuex);

const vuexSession = new VuexPersistence<State>({
  storage: window.sessionStorage,
  reducer: (state) => ({
    organisations: state.organisations,
    projects: state.projects,
    users: state.users,
    widget: state.widget,
    rules: state.rules,
    dataSets: state.dataSets,
    locationsV2: state.locationsV2,
    jobs: state.jobs,
    explorer: state.explorer,
    accounts: state.accounts,
  }),
  filter: (mutation) =>
    mutation.type === 'selectOrganisation' ||
    mutation.type === 'selectAccount' ||
    mutation.type === 'addOrganisations' ||
    mutation.type === 'selectProject' ||
    mutation.type === 'addProject' ||
    mutation.type === 'addProjects' ||
    mutation.type === 'updateProject' ||
    mutation.type === 'addWidgetFilters' ||
    mutation.type === 'removeAllWidgetFilters' ||
    mutation.type === 'removeWidgetFilter' ||
    mutation.type === 'addDataSet' ||
    mutation.type === 'createRule' ||
    mutation.type === 'removeRule' ||
    mutation.type === 'addLocationFilter' ||
    mutation.type === 'removeLocationFilter' ||
    mutation.type === 'toggleShowPositions' ||
    mutation.type === 'toggleShowAddPositionDialog' ||
    mutation.type === 'addActiveJob' ||
    mutation.type === 'addCompletedJob' ||
    mutation.type === 'clearJobs' ||
    mutation.type === 'removeCompletedJobs' ||
    mutation.type === 'setLastView' ||
    mutation.type === 'clearLastView',
});

const vuexLocal = new VuexPersistence<State>({
  storage: window.localStorage,
  reducer: (state) => ({
    login: state.login,
    datatable: state.datatable,
    subscriptions: state.subscriptions,
    notifications: state.notifications,
    gateways: state.gateways,
  }),
  filter: (mutation) => mutation.type === 'addToken' ||
    mutation.type === 'removeToken' ||
    mutation.type === 'impersonate' ||
    mutation.type === 'stopImpersonate' ||
    mutation.type === 'updatePermissions' ||
    mutation.type === 'setLastOrganisation' ||
    mutation.type === 'setExperimental' ||
    mutation.type === 'setItemsPerPage' ||
    mutation.type === 'toggleShowAllOrganisations' ||
    mutation.type === 'setSubscriptionViewLayout' ||
    mutation.type === 'dismissNotificationBanner' ||
    mutation.type === 'showNotificationBanner' ||
    mutation.type === 'removeToastErrorMessage',
});


export const store = new Vuex.Store<State>({
  modules: {
    users,
    devices,
    gateways,
    loading,
    login,
    organisations,
    versions,
    batches,
    projects,
    sites,
    locations,
    notifications,
    sidebar,
    applications,
    adminApplications,
    tasks,
    roles,
    reporting,
    widget,
    rules,
    dataSets,
    locationsV2,
    jobs,
    explorer,
    datatable,
    subscriptions,
    accounts,
  },
  mutations: {
    reset(state: any) {
      window.sessionStorage.clear();
      Object.keys(state).forEach((key) => {
        Object.assign(state[key], (state[key].initialState ? state[key].initialState() : state[key]));
      });
    },
    fullReset(state: any) {
      window.localStorage.clear();
      window.sessionStorage.clear();
    },
  },
  plugins: [vuexSession.plugin, vuexLocal.plugin],
});

export default store;
